import { useEffect, useReducer } from 'react';
import { getValidDeviceTypes } from 'shared/utils/formatters';
import { getBuilding } from 'shared/api/buildings';
import { getBuildingUpgrades } from 'shared/api/upgrades';
import {
  getTenant,
  getTenantBuilding,
  getTenantMacs,
  getTenantSubscription,
  getTenantUnit,
} from 'shared/api/tenants';
import { getSmartzone } from 'shared/api/wireless';
import { getUnit } from 'shared/api/units';
import { useRequest } from 'shared/hooks';
import { processDevices } from './moveTenant';
import { getOnboardAccountByTenantId } from '../api/onboard';

const initialState = {
  moveComplete: false,
  moveCount: 0,
  newBuilding: null,
  newUnit: null,
  tenant: null,
};

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'tenant':
      return {
        ...initialState,
        moveComplete: false,
        newBuilding: state.newBuilding,
        newUnit: state.newUnit,
        tenant: payload,
      };
    case 'building':
      return {
        ...state,
        loadError: null,
        moveComplete: false,
        newBuilding: payload,
        newUnit: null,
      };
    case 'unit':
      return {
        ...state,
        moveComplete: false,
        newUnit: payload,
      };
    case 'loadError':
      return {
        ...state,
        loadError: payload || 'failed to load all required data, please try again later.',
        moveComplete: false,
      };
    case 'move': {
      const currentUnit = {
        ...state.newUnit,
        tenant_count: state.newUnit.tenant_count + 1,
      };
      return {
        ...state,
        currentBuilding: state.newBuilding,
        currentUnit,
        moveComplete: true,
        moveCount: state.moveCount + 1,
        newUnit: currentUnit,
      };
    }
    case 'state':
      return {
        ...state,
        ...payload,
      };
    default:
      return state;
  }
}

// Hooks for loading all tenant move state.
export default function useTenantMoveState(buildingId, unitId, tenantId) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { request } = useRequest();
  const {
    newBuilding,
    newUnit,
    tenant,
    moveComplete,
  } = state;

  // Sync newBuilding to ?building_id url param.
  useEffect(() => {
    (async () => {
      if (buildingId && (!newBuilding || newBuilding.id !== buildingId)) {
        const result = await request(getBuilding(buildingId, '?expand=nas'));
        if (result) {
          dispatch({
            action: 'building',
            payload: result,
          });
        }
      } else if (!buildingId) {
        dispatch({
          action: 'building',
          payload: null,
        });
      }
    })();
  }, [request, newBuilding, buildingId]);

  // Sync newUnit to ?unit_id url param.
  useEffect(() => {
    (async () => {
      if (newBuilding && unitId && (!newUnit || newUnit.id !== unitId)) {
        const result = await request(getUnit(unitId));
        if (result) {
          dispatch({
            action: 'unit',
            payload: result,
          });
        }
      } else if (!unitId) {
        dispatch({
          action: 'unit',
          payload: null,
        });
      }
    })();
  }, [request, newBuilding, newUnit, unitId]);

  // Sync tenant to ?tenant_id url param.
  useEffect(() => {
    (async () => {
      if (tenantId && (!tenant || tenant.id !== tenantId)) {
        const result = await request(getTenant(tenantId));
        if (result) {
          dispatch({
            action: 'tenant',
            payload: result,
          });
        }
      } else if (!tenantId) {
        dispatch({
          action: 'tenant',
          payload: null,
        });
      }
    })();
  }, [request, tenant, tenantId]);

  // Loads extra data about the selected tenant and building.
  useEffect(() => {
    (async () => {
      if (!tenantId || !newUnit || !newBuilding || !tenant || moveComplete) return;

      const result = await Promise.all([
        request(getBuildingUpgrades(newBuilding.id)),
        request(getTenantBuilding(tenantId)),
        request(getTenantMacs('', '', tenantId)),
        request(getTenantSubscription(tenantId)),
        request(getTenantUnit(tenantId)),
        request(getOnboardAccountByTenantId(tenantId)),
      ]);
      if (result.some((r) => r === undefined)) return;
      if (result.some((r) => !r)) {
        dispatch({ action: 'loadError' });
        return;
      }

      // Make sure these are in the same order as the Promise.all
      const [
        newBuildingUpgrades,
        { data: tenantBuilding },
        { rows: devices },
        { data: subscription },
        { data: tenantUnit },
        { data: onboardAccount },
      ] = result;

      const hasDevices = devices.length > 0;
      const hasSubscription = !!subscription;

      if ((hasDevices || hasSubscription) && !onboardAccount) {
        dispatch({
          action: 'loadError',
          payload: 'tenant has devices/subscription without Onboard account',
        });
        return;
      }

      const {
        disallowReason,
        ethernetEnabled,
        smartzoneEnabled,
      } = getValidDeviceTypes(newBuilding, newUnit);

      let smartzone;
      if (smartzoneEnabled) {
        smartzone = await request(getSmartzone(newBuilding.dot1x.smartzone_id));
        if (smartzone === undefined) return;
        if (!smartzone) {
          dispatch({ action: 'loadError' });
          return;
        }
      }
      const deviceActions = processDevices(devices, ethernetEnabled);

      const isSameUnit = tenantUnit && tenantUnit.id === newUnit.id;
      const isSameBuilding = tenantBuilding && tenantBuilding.id === newBuilding.id;

      // Whether we need to update or cancel the tenants subscription after the move.
      const forceSubscriptionChange = hasSubscription
        && !subscription.voucher
        && newBuildingUpgrades.length > 0
        && !newBuildingUpgrades.some(({ plan_id: plan }) => plan === subscription.plan_id);

      // A tenant moves to a new building that offers the same plan and is keeping the subscription
      const movingSamePlan = hasSubscription
        && !subscription.voucher
        && !isSameBuilding
        && newBuildingUpgrades.length > 0
        && newBuildingUpgrades.some(({ plan_id: plan }) => plan === subscription.plan_id);

      // Whether we need to cancel subscription before move.
      const forceSubscriptionCancel = hasSubscription
        && !isSameBuilding
        && (subscription.voucher || disallowReason || newBuildingUpgrades.length === 0);

      const groupDpskWarning = smartzone && !isSameUnit;
      const newState = {
        currentBuilding: tenantBuilding,
        currentUnit: tenantUnit,
        deviceActions,
        devices,
        forceSubscriptionCancel,
        forceSubscriptionChange,
        movingSamePlan,
        groupDpskWarning,
        hasDevices,
        hasSubscription,
        internetDisallowReason: disallowReason,
        isOrphan: !tenantUnit || !tenantBuilding,
        isSameBuilding,
        isSameUnit,
        loadError: '', // Setting this to empty string means TenantMoveForm can render.
        moveComplete: false,
        newBuildingUpgrades,
        subscription,
      };
      dispatch({
        action: 'state',
        payload: newState,
      });
    })();
  }, [request, tenantId, newUnit, newBuilding, moveComplete, tenant]);

  return [state, dispatch];
}
